import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  type PassportEventDto,
  PassportEventDto as PassportEventDtoSchema,
} from '@tunodo/ticketing-shared';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_app/admin/passport/new')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden crear eventos.');
      throw redirect({ to: '/' });
    }
  },
  component: PassportNewEventPage,
});

function PassportNewEventPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [ticketingEventId, setTicketingEventId] = useState('');

  const { data: ticketingEvents, isLoading: eventsLoading } = useQuery({
    queryKey: ['ticketing-events', 'available'],
    queryFn: () =>
      apiFetch('/api/passport-admin/ticketing-events?available=true', {
        schema: z.array(z.object({ id: z.string(), name: z.string(), date: z.string() })),
      }),
  });

  const mutation = useMutation<PassportEventDto>({
    mutationFn: () => {
      const body: { title: string; ticketingEventId: string; date?: string } = {
        title,
        ticketingEventId,
      };
      if (date) body.date = new Date(date).toISOString();
      return apiFetch('/api/passport-admin/events', {
        method: 'POST',
        body: JSON.stringify(body),
        schema: PassportEventDtoSchema,
      });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'events'] });
      toast.success('Evento creado.');
      void navigate({ to: '/admin/passport/$eventId', params: { eventId: created.id } });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('El título es obligatorio.');
      return;
    }
    if (!ticketingEventId) {
      toast.error('Debes seleccionar un evento de ticketing.');
      return;
    }
    mutation.mutate();
  };

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/passport">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Nuevo evento</h1>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Datos del evento</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                placeholder="Ej. Congreso Nacional de Medicina"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Evento de ticketing *</Label>
              <Select
                value={ticketingEventId}
                onValueChange={setTicketingEventId}
                disabled={eventsLoading || mutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={eventsLoading ? 'Cargando...' : 'Selecciona un evento'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(ticketingEvents ?? []).map((ev) => (
                    <SelectItem key={ev.id} value={ev.id}>
                      {ev.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date">Fecha (opcional)</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Guardando…' : 'Crear evento'}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/admin/passport">Cancelar</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
